import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import users from '../../api/users';
import api from '../../api';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const VERIFY_INSTAGRAM_REQUEST = 'app/ProfileSettingsPage/VERIFY_INSTAGRAM_REQUEST';
export const VERIFY_INSTAGRAM_SUCCESS = 'app/ProfileSettingsPage/VERIFY_INSTAGRAM_SUCCESS';
export const VERIFY_INSTAGRAM_ERROR = 'app/ProfileSettingsPage/VERIFY_INSTAGRAM_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  verifyInstagramInProgress: false,
  verifyInstagramError: null,
  verifyInstagramDone: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case VERIFY_INSTAGRAM_REQUEST:
      return {
        ...state,
        verifyInstagramInProgress: true,
        verifyInstagramError: null,
        verifyInstagramDone: false,
      };
    case VERIFY_INSTAGRAM_SUCCESS:
      return {
        ...state,
        verifyInstagramInProgress: false,
        verifyInstagramDone: payload.isVerified,
      };
    case VERIFY_INSTAGRAM_ERROR:
      return { ...state, verifyInstagramInProgress: false, verifyInstagramError: payload };

    case CLEAR_UPDATED_FORM:
      return {
        ...state,
        updateProfileError: null,
        uploadImageError: null,
        verifyInstagramError: null,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const verifyInstagramRequest = () => ({
  type: VERIFY_INSTAGRAM_REQUEST,
});
export const verifyInstagramSuccess = isVerified => ({
  payload: { isVerified },
  type: VERIFY_INSTAGRAM_SUCCESS,
});
export const verifyInstagramError = error => ({
  type: VERIFY_INSTAGRAM_ERROR,
  payload: error,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = (actionPayload, certificateMaybe, disciplineCertificateToDelete) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updateProfileRequest());

  const deleteOps = Array.isArray(disciplineCertificateToDelete)
    ? disciplineCertificateToDelete
    : disciplineCertificateToDelete
    ? [disciplineCertificateToDelete]
    : [];

  const uploadOps = Array.isArray(certificateMaybe)
    ? certificateMaybe
    : certificateMaybe
    ? [certificateMaybe]
    : [];

  const deletePromises = deleteOps.map(d => users.deleteCertificate(d));
  
  // We handle document deletion via protectedData update, effectively replacing the list.
  // HOWEVER, if we delete here via Sharetribe SDK Update, the Backend SYNC might not know.
  // If the backend is "Source of Truth", we might need to tell it "Delete this key".
  // Current frontend flow: ProfileActivities -> onUpdateProfile(profileData) 
  // -> calls updateProfile here with profileData containing the NEW list (documents removed).
  // -> calls sdk.currentUser.updateProfile(cleanPayload).
  // Sharetribe gets updated. Documents are gone in ProtectedData.
  // BUT the Backend sees the change? 
  // If the backend populates PublicData from ProtectedData, does it listen to Sharetribe webhooks?
  // Likely NOT. It probably updates PublicData only when we call the /users/files endpoint (UPLOAD).
  
  // FIX: To ensure Backend SYNCs PublicData after a delete, we must explicitly call the proper backend delete endpoint
  // if available, OR we rely on the Backend syncing via a hook.
  // Given "backend creates and maintains public documents... publicData is read back... by getUserBody...",
  // if we update Sharetribe directly, the Mongo Backned might be out of sync until next login or action.
  //
  // CRITICAL: We need a way to tell the backend "I deleted this doc, please re-sync PublicData".
  // The 'deleteCertificate' function calls `users.deleteCertificate`. 
  // Let's check `users.deleteCertificate` in `src/api/users/index.js`.
  // It calls `httpClient.delete('users/files'...)`.
  //
  // SO: We must ensure `disciplineCertificateToDelete` is populated correctly OR we explicitly trigger a sync.
  // ProfileActivities.js calls `onUpdateProfile(profileData)` without 3rd arg for `disciplineCertificateToDelete`!
  // It just updates the JSON list.
  
  // RESULT: Sharetribe has the file deleted (in protected list).
  // Backend (Mongo/PublicData) thinks it is still there because no one told it to re-derive PublicData.
  // UI reads from PublicData (populated by Backend), so it sees the "Ghost" file.
  
  // SOLUTION: 
  // 1. We must pass the `key` or identifier of the deleted document to `onUpdateProfile`.
  // 2. onUpdateProfile must call `users.deleteCertificate` to trigger the backend sync.
  
  const uploadSequentially = async ops => {
    const results = [];
    for (const op of ops) {
      const files = op.certificates || op.certificate;
      const disciplines = op.disciplines || (op.discipline ? [op.discipline] : []);
      const metadata = op.metadata || {}; // Complete document metadata
      const profileData = op.profileData || {}; // Activities, selfCertifications, etc.
      
      const res = await users.uploadCertificate(files, disciplines, metadata, profileData);
      
      console.log('[Duck] Raw backend response:', res);
      
      // Backend now returns the complete saved documents
      // Backend handles Sharetribe update, so we just need to refresh
      let uploadedFiles = res?.data?.documents || res?.data?.certificate || res?.data?.data || res?.data;
      
      console.log('[Duck] Parsed uploadedFiles:', uploadedFiles);
      
      if (Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
        results.push({
          disciplines,
          files: uploadedFiles
        });
        console.log('[Duck] Added to results:', { disciplines, files: uploadedFiles });
      } else {
        console.warn('[Duck] Unexpected upload response format:', res);
        console.warn('[Duck] Could not extract files array from response');
      }
    }
    console.log('[Duck] Final upload results:', results);
    return results;
  };

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  // If there are certificate operations, handle them first
  if (uploadOps.length > 0 || deleteOps.length > 0) {
    const certificatePromise = Promise.all(deletePromises).then(() => uploadSequentially(uploadOps));
    
    return certificatePromise
      .then((uploadResults) => {
        console.log('[Duck] Certificate operations complete. Backend handled Sharetribe update.');
        console.log('[Duck] Upload results:', uploadResults);
        
        // If we have OTHER profile data to update (activities, bio, etc.), update it now
        if (actionPayload && (actionPayload.publicData || actionPayload.privateData || actionPayload.protectedData || actionPayload.bio)) {
          console.log('[Duck] Updating profile with additional data:', actionPayload);
          return sdk.currentUser
            .updateProfile(actionPayload, queryParams)
            .then(response => {
              dispatch(updateProfileSuccess(response));
              const entities = denormalisedResponseEntities(response);
              if (entities.length !== 1) {
                throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
              }
              const currentUser = entities[0];
              dispatch(currentUserShowSuccess(currentUser));
            })
            .catch(e => {
              throw e;
            });
        } else {
          // No additional profile data, just refresh from Sharetribe
          console.log('[Duck] No additional data to update. Refreshing user data from Sharetribe...');
          return sdk.currentUser.show(queryParams)
            .then(response => {
              dispatch(updateProfileSuccess(response));
              const entities = denormalisedResponseEntities(response);
              if (entities.length !== 1) {
                throw new Error('Expected a resource in the sdk.currentUser.show response');
              }
              const currentUser = entities[0];
              dispatch(currentUserShowSuccess(currentUser));
            })
            .catch(e => {
              throw e;
            });
        }
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  } else {
    // No certificate operations, just update profile directly
    console.log('[Duck] Direct profile update - no certificate ops');
    console.log('[Duck] Token check:', sdk.tokenStore?.getToken?.()?.access_token ? 'Token exists' : 'NO TOKEN');
    console.log('[Duck] ActionPayload:', actionPayload);
    console.log('[Duck] Query params:', queryParams);
    
    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        console.log('[Duck] Profile update SUCCESS:', response);
        dispatch(updateProfileSuccess(response));
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => {
        console.error('[Duck] Profile update FAILED:', e);
        console.error('[Duck] Error status:', e.status);
        console.error('[Duck] Error response:', e.response);
        dispatch(updateProfileError(storableError(e)));
      });
  }
};

export const verifyInstagram = token => (dispatch, getState, sdk) => {
  dispatch(verifyInstagramRequest());

  return api.users
    .verifyInstagram(token)
    .then(res => {
      const { user } = res?.data || {};
      const { isInstagramVerified } = user?.attributes?.profile?.metadata || {};
      dispatch(verifyInstagramSuccess(!!isInstagramVerified));
    })
    .catch(e => {
      console.log(e);
      dispatch(verifyInstagramError(e));
    });
};
